import { z } from "zod";

/**
 * API Input Schemas
 * Validation schemas for auth API requests
 */

export const SignupInputSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  name: z.string().min(1, "Name is required"),
  username: z.string().min(3, "Username must be at least 3 characters"),
});

export type SignupInput = z.infer<typeof SignupInputSchema>;

export const LoginInputSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export type LoginInput = z.infer<typeof LoginInputSchema>;

// The server enrols the key for whoever the session belongs to; it never takes a username.
export const UpdateBiometricsKeyInputSchema = z.object({
  publicKey: z.string().nullish(),
});

export type UpdateBiometricsKeyInput = z.infer<typeof UpdateBiometricsKeyInputSchema>;

export const LoginByBiometricsInputSchema = z.object({
  challengeToken: z.string().min(1, "challengeToken is reqired"),
  biometricsToken: z.string().min(1, "biometricsToken is required"),
});

export type LoginByBiometricsInput = z.infer<typeof LoginByBiometricsInputSchema>;

/**
 * API Response Schemas
 * Validation schemas for auth API responses
 */

export const ApiErrorResponseSchema = z.object({
  message: z.string().optional(),
  error: z.string().optional(),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;

export const ApiUserSchema = z
  .object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    username: z.string().optional(),
    displayUsername: z.string().optional(),
    role: z.string().optional(),
    // mybestuser OAuth2 fields
    authSource: z.enum(["local", "mybestuser"]).optional(),
    myBestUserId: z.string().nullable().optional(),
    myBestUserRole: z.string().nullable().optional(),
    // Contract expiry (ISO string from school's activated contract)
    ticketExpiresAt: z.string().nullable().optional(),
    // Profile picture URL (CDN URL after upload)
    profilePictureUrl: z.string().nullable().optional(),
  })
  .transform((user) => ({
    id: user.id,
    email: user.email,
    name: user.name,
    username: user.username || "",
    displayUsername: user.displayUsername || user.username || "",
    role: user.role || "student",
    authSource: user.authSource || "local",
    myBestUserId: user.myBestUserId || null,
    myBestUserRole: user.myBestUserRole || null,
    ticketExpiresAt: user.ticketExpiresAt ?? null,
    profilePictureUrl: user.profilePictureUrl ?? null,
  }));

// Output type after transformation (all fields are required strings)
export type ApiUser = z.output<typeof ApiUserSchema>;

/** Which API deployment a client should talk to, decided from the user's school country. */
export const ApiCloudSchema = z.enum(["global", "cn"]);

export type ApiCloud = z.infer<typeof ApiCloudSchema>;

export const ApiAuthResponseSchema = z.object({
  token: z.string().optional(),
  session: z
    .object({
      token: z.string().optional(),
    })
    .optional(),
  user: ApiUserSchema.optional(),
  apiCloud: ApiCloudSchema.optional(),
});

export type ApiAuthResponse = z.infer<typeof ApiAuthResponseSchema>;

export const AuthResponseSchema = z.object({
  token: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    username: z.string(),
    displayUsername: z.string(),
    role: z.string(),
    authSource: z.enum(["local", "mybestuser"]),
    myBestUserId: z.string().nullable(),
    myBestUserRole: z.string().nullable(),
    ticketExpiresAt: z.string().nullable().optional(),
    profilePictureUrl: z.string().nullable().optional(),
    publicKey: z.string().nullish(),
  }),
  apiCloud: ApiCloudSchema.optional(),
});

export type AuthResponse = z.infer<typeof AuthResponseSchema>;
